import { useState } from 'react';
import { Alert, Badge, Button, Modal, Skeleton, Table } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { isCalibrationMissing, useSiloCalibration } from '../api';
import type { LocationDto } from '../types';
import { CalibrationForm } from './CalibrationForm';

/** One row of the calibration register (BUILD_SPEC.md §5.2/§5.6/§11 Settings —
 * silo calibration config is this goal's sole ownership, plan.md QC fix #3). */
export function CalibrationRow({ location }: { location: LocationDto }) {
  const [opened, setOpened] = useState(false);
  const calibrationQuery = useSiloCalibration(location.id);
  const missing = isCalibrationMissing(calibrationQuery.error);
  const calibration = calibrationQuery.data ?? null;

  return (
    <Table.Tr>
      <Table.Td>{location.code}</Table.Td>
      <Table.Td>{location.name}</Table.Td>
      <Table.Td>
        {calibrationQuery.isLoading ? (
          <Skeleton height={20} width={120} />
        ) : calibration ? (
          <Badge color="brandGreen" variant="light">
            {calibration.method} · {calibration.densitySource}
          </Badge>
        ) : missing ? (
          <Badge color="amber" variant="light">
            Not configured
          </Badge>
        ) : (
          <Badge color="brandRed" variant="light">
            Error
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Button variant="subtle" size="compact-sm" onClick={() => setOpened(true)}>
          {calibration ? 'Edit' : 'Configure'}
        </Button>
      </Table.Td>

      <Modal opened={opened} onClose={() => setOpened(false)} title={`Silo calibration — ${location.name}`} size="lg">
        {calibrationQuery.isLoading ? (
          <Skeleton height={200} />
        ) : !missing && calibrationQuery.isError ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {calibrationQuery.error instanceof ApiRequestError ? calibrationQuery.error.message : 'Could not load calibration.'}
          </Alert>
        ) : (
          <CalibrationForm locationId={location.id} calibration={calibration} onSaved={() => setOpened(false)} />
        )}
      </Modal>
    </Table.Tr>
  );
}
